/**
 * Transform snake_case API responses to camelCase for frontend consumption.
 * Special mappings: property_name -> property, asset_type -> type, loan_amount -> loanAmount, etc.
 */

const KEY_MAP: Record<string, string> = {
  property_name: 'property',
  asset_type: 'type',
  loan_amount: 'loanAmount',
  sponsor_credit: 'sponsorCredit',
  stage_index: 'stageIndex',
  days_open: 'daysOpen',
  days_in_status: 'daysInStatus',
  total_days_open: 'totalDaysOpen',
  maturity_date: 'maturity',
  cash_flows: 'cashFlows',
  market_data: 'market',
  trigger_type: 'type',
  due_date: 'due',
  projected_noi: 'projectedNOI',
  default_probability: 'defaultProbability',
  spread_to_treasury: 'spreadToTreasury',
  captured_at: 'capturedAt',
  created_at: 'createdAt',
  updated_at: 'updatedAt',
  resolved_at: 'resolvedAt',
  loan_id: 'loanId',
}

function toCamel(str: string): string {
  if (KEY_MAP[str]) return KEY_MAP[str]
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

function transformKeys(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) return obj.map(transformKeys)
  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj)) {
      const key = toCamel(k)
      result[key] = transformKeys(v)
    }
    return result
  }
  return obj
}

export function toCamelCase<T>(data: unknown): T {
  return transformKeys(data) as T
}

function toSnake(str: string): string {
  return str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
}

function snakeKeys(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) return obj.map(snakeKeys)
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj)) result[toSnake(k)] = snakeKeys(v)
    return result
  }
  return obj
}

/**
 * The way back for a request body: a block the API sent, read through
 * `toCamelCase`, goes back to it in the spelling it came in.
 *
 * Generic only. The special mappings above (`property_name` → `property`) are
 * lossy — `type` could be `asset_type` or `trigger_type` — so they are not
 * inverted; a body that carries one of those keys spells it in snake_case
 * itself.
 */
export function toSnakeCase<T>(data: unknown): T {
  return snakeKeys(data) as T
}
